import AddButton from "./AddButton";
import AnnotationEntry from "./AnnotationEntry";
import { useState } from "react";
import AnnotationModal from "./AnnotationModal";
import { IconButton, Collapse } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useViewMode } from "../contexts/ViewModeContext";
import InfoTooltip from "./InfoTooltip";
import { TimeSignatureInstruction } from "../lib/definitions/SongLevelAnnotation";

/**
 * TimeSignatureSection component for adding and editing time signature changes
 * Updated to be collapsible and take less space
 */
const TimeSignatureChangeSection = ({ timeSignatureChanges, onChange, measures }) => {
  // Common time signatures for dropdown
  const timeSignatureOptions = ["4/4", "3/4", "2/4", "6/8", "9/8", "12/8", "Custom"];

  // State for modal
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newTimeSignatureChange, setNewTimeSignatureChange] = useState({
    measure: "1",
    timeSignature: "4/4"
  });
  const [customTimeSignature, setCustomTimeSignature] = useState({
    numerator: 4,
    denominator: 4
  });
  const [isCustom, setIsCustom] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);

  // View mode
  const {isViewMode} = useViewMode();

  // Add a new time signature change
  const handleAddClick = () => {
    setNewTimeSignatureChange({
      measure: "1",
      timeSignature: "4/4"
    });
    setEditingIndex(null);
    setShowForm(true);
  };

  // Edit an existing time signature change
  const handleEditClick = (index) => {
    const change = timeSignatureChanges[index];
    const value = change.timeSignature;
    
    // Check if this is a custom time signature
    const isStandardTimeSignature = timeSignatureOptions.includes(value) && value !== "Custom";
    
    if (!isStandardTimeSignature) {
      // Parse the custom time signature (e.g., "5/8")
      const [numerator, denominator] = value.split('/').map(num => parseInt(num, 10));
      
      if (!isNaN(numerator) && !isNaN(denominator)) {
        setCustomTimeSignature({
          numerator,
          denominator
        });
        setIsCustom(true);
        
        // Set value to "Custom" to show the custom inputs
        setNewTimeSignatureChange({
          measure: String(change.measure),
          timeSignature: "Custom"
        });
      } else {
        // Fallback if parsing fails
        setNewTimeSignatureChange({
          measure: String(change.measure),
          timeSignature: value
        });
        setIsCustom(false);
      }
    } else {
      // Standard time signature
      setNewTimeSignatureChange({
        measure: String(change.measure),
        timeSignature: value
      });
      setIsCustom(false);
    }
    
    setEditingIndex(index);
    setShowForm(true);
  };

  // Delete a time signature change
  const handleDelete = (index) => {
    const newChanges = [...timeSignatureChanges];
    newChanges.splice(index, 1);
    onChange(newChanges);
  };

  // Handle measure number change
  const handleMeasureChange = (e) => {
    // Allow any input, including empty string
    setNewTimeSignatureChange({ ...newTimeSignatureChange, measure: e.target.value });
  };
  
  // Handle time signature change
  const handleTimeSignatureChange = (e) => {
    const value = e.target.value;
    setNewTimeSignatureChange({ ...newTimeSignatureChange, timeSignature: value });
    
    // Check if custom option is selected
    if (value === "Custom") {
      setIsCustom(true);
    } else {
      setIsCustom(false);
    }
  };
  
  // Handle custom numerator change
  const handleCustomNumeratorChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setCustomTimeSignature({ ...customTimeSignature, numerator: value });
    }
  };
  
  // Handle custom denominator change
  const handleCustomDenominatorChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && [1, 2, 4, 8, 16, 32, 64].includes(value)) {
      setCustomTimeSignature({ ...customTimeSignature, denominator: value });
    }
  };

  // Save time signature change
  const handleSave = () => {
    let timeSignatureValue = newTimeSignatureChange.timeSignature;
    
    // If custom is selected, use the custom time signature
    if (isCustom) {
      timeSignatureValue = `${customTimeSignature.numerator}/${customTimeSignature.denominator}`;
    }
    
    // Clear previous errors
    setError("");
    
    // Validate measure number
    if (!newTimeSignatureChange.measure) {
      setError("Please enter a valid measure number");
      return;
    }
    
    // Convert measure to number and validate
    const measureNum = parseInt(newTimeSignatureChange.measure, 10);
    // COMMENTED OUT: Measure range validation
    // if (isNaN(measureNum) || measureNum < 1 || measureNum > measures) {
    //   setError(`Please enter a valid measure number between 1 and ${measures}`);
    //   return;
    // }
    
    const updatedTimeSignatureChange = {
      ...newTimeSignatureChange,
      measure: measureNum, // Convert to number
      timeSignature: timeSignatureValue
    };
    
    // COMMENTED OUT: Chronological order check
    // Make sure new changes are in chronological order
    // const maxExisting = timeSignatureChanges
    //   .filter((_, idx) => idx !== editingIndex)
    //   .reduce((max, row) => Math.max(max, row.measure), 0);
  
    // if (measureNum <= maxExisting) {
    //   setError(
    //     `Measure must be greater than ${maxExisting}, the last defined time signature`
    //   );
    //   return;
    // }

    // Update or add the time signature change
    let newChanges = [...timeSignatureChanges];
    if (editingIndex !== null) {
      newChanges[editingIndex] = updatedTimeSignatureChange;
    } else {
      // Generate a new ID
      const newId = newChanges.length > 0 ? Math.max(...newChanges.map(row => row.id)) + 1 : 1;
      newChanges.push({ ...updatedTimeSignatureChange, id: newId });
    }
    
    // Sort by measure number
    newChanges.sort((a, b) => a.measure - b.measure);
    
    onChange(newChanges);
    setShowForm(false);
    setIsCustom(false);
  };

  // Cancel form
  const handleCancel = () => {
    setShowForm(false);
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  // Get a summary of tempo changes for the collapsed view
  const getSummary = () => {
    if (timeSignatureChanges.length === 0) {
      return "No time signature changes";
    } else if (timeSignatureChanges.length === 1) {
      const ts = timeSignatureChanges[0];
      return `1 time signature change: ${ts.timeSignature} at bar ${ts.measure}`;
    } else {
      return `${timeSignatureChanges.length} time signature changes`;
    }
  };

  return (
    <div className="mb-4 rounded-md overflow-hidden point">
      {/* Header with expand/collapse */}
      <div 
        className="flex items-center px-3 py-2 bg-gray-50 cursor-pointer"
        onClick={toggleExpand}
      >
        <h3 className="text-base font-medium m-0"> Time Signature (拍号) </h3>
        <div className="flex items-center ml-auto">
          <span className="text-sm text-gray-500 mr-2">{!expanded && getSummary()}</span>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleExpand(); }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>
        <InfoTooltip instruction={TimeSignatureInstruction} />
      </div>

      {/* Collapsible content */}
      <Collapse in={expanded}>
        <div className="px-3 py-2">
          <div className="flex flex-wrap items-center" >
            {timeSignatureChanges.map((change, index) => (
              <div key={index} className="mr-2 mb-2">
                <AnnotationEntry
                  label={`${change.measure}: ${change.timeSignature}`}
                  onDelete={() => handleDelete(index)}
                  onClick={() => handleEditClick(index)}
                />
              </div>
            ))}
            {!isViewMode && <AddButton onClick={handleAddClick} />}
          </div>
          </div>
      </Collapse>

      {/* Modal for adding/editing */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <AnnotationModal
            title={editingIndex !== null ? "Edit Time Signature" : "Add Time Signature"}
            onSave={handleSave}
            onCancel={handleCancel}
            isEditing={editingIndex !== null}
            error={error}
          >
            <div className="mb-4">
              <label htmlFor="measure" className="block text-sm font-medium mb-1">
                Measure Number
              </label>
              <input
                id="measure"
                type="number"
                min="1"
                max={measures}
                value={newTimeSignatureChange.measure}
                onChange={handleMeasureChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Enter measure number"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="timeSignature" className="block text-sm font-medium mb-1">
                Time Signature
              </label>
              <select
                id="timeSignature"
                value={newTimeSignatureChange.timeSignature}
                onChange={handleTimeSignatureChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {timeSignatureOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            {isCustom && (
              <div className="flex items-end space-x-2 mb-4">
                <div className="flex-1">
                  <label htmlFor="numerator" className="block text-sm font-medium mb-1">
                    Numerator
                  </label>
                  <input
                    id="numerator"
                    type="number"
                    min="1"
                    max="32"
                    value={customTimeSignature.numerator}
                    onChange={handleCustomNumeratorChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="pb-2 text-xl">/</div>
                <div className="flex-1">
                  <label htmlFor="denominator" className="block text-sm font-medium mb-1">
                    Denominator
                  </label>
                  <select
                    id="denominator"
                    value={customTimeSignature.denominator}
                    onChange={handleCustomDenominatorChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={4}>4</option>
                    <option value={8}>8</option>
                    <option value={16}>16</option>
                    <option value={32}>32</option>
                    <option value={64}>64</option>
                  </select>
                </div>
              </div>
            )}
          </AnnotationModal>
        </div>
      )}
    </div>
  );
}

export default TimeSignatureChangeSection;
